'use client';

import { useContext, useEffect, useState, type ReactNode } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { CustomButton } from '@/components/common/button';
import { useSubscription } from '@/hooks/usePricing';
import { parseDateOnly } from '@/lib/utils';
import dynamic from 'next/dynamic';
import {
  AutoRechargeExample,
  MarkupExample,
  Callout,
  CapacityScale,
  ChipRow,
  FaqList,
  FlowSteps,
  HierarchyDiagram,
  IconTile,
  PointGrid,
  SectionLabel,
  SimpleTable,
  StepHeader,
  TourFocusContext,
} from './blocks';
import {
  ACCESS_LEVELS,
  ACCOUNT_SETTINGS,
  AGENCY_MARKUP_FAQ,
  AGENCY_MARKUP_FLOW,
  AGENCY_MARKUP_WHERE,
  PIXEL_FAQ,
  PIXEL_PLATFORM_GUIDES,
  PIXEL_TROUBLESHOOTING,
  PIXEL_FLOW,
  PIXEL_PATH,
  PIXEL_USES,
  ADD_ONS,
  BILLING_RULES,
  FAMILIES,
  PAYMENT_METHOD_POINTS,
  SUBSCRIBE_FLOW,
  TRIAL_DAYS,
  ITEM_DESCRIPTIONS,
  ORG_CONCEPTS,
  ORG_FAQ,
  PERMISSION_EXAMPLES,
  PLAN_DIFFERENTIATORS,
  PLAN_TIERS,
  SCOPE_SWITCH_POINTS,
  SUBSCRIPTION_FAQ,
  SUB_TENANT_TYPES,
  TEAM_FAQ,
  TEAM_FLOW,
  WALLET_FAQ,
  WALLET_FLOW,
  WALLET_SPEND_CATEGORIES,
  WORKFLOW,
} from './content';

// Only rendered inside one onboarding step — keep the integrations page chunk out of the root bundle.
const TrackingPixelTab = dynamic(() => import('@/app/integrations/TrackingPixelTab').then((m) => m.TrackingPixelTab), { ssr: false });

// ----------------------------------------------------------------------

export type NavLeaf = { title: string; path: string; icon?: ReactNode; info?: string };
export type NavItemLike = NavLeaf & { children?: NavLeaf[] };
export type ModuleGroup = { key: string; items: NavItemLike[] };

export type StepContext = {
  orgName?: string;
  accountName?: string;
  roleNames: string[];
  isOwner: boolean;
  isSubAccountMember: boolean;
  approvalPolicy?: { requires_approval_for_publish: boolean; requires_approval_for_spend: boolean; spend_threshold: number };
  modules: ModuleGroup[];
  canOpen: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
  goTo: (path: string) => void;
  jumpToStep: (id: string) => void;
  subscriptionPath: string;
  walletPath: string;
  /** User whose pricing subscription applies — the org owner's for sub-tenant members, same as the Subscription page. */
  pricingUserId: number | null;
  /** Users granted blyn.view only — hides every Blyn mention otherwise. */
  showBlyn: boolean;
};

function OpenLink({ ctx, label, path, permission }: { ctx: StepContext; label: string; path: string; permission?: string }) {
  if (permission && !ctx.canOpen(permission)) return null;
  return (
    <CustomButton
      variant="text"
      onClick={() => ctx.goTo(path)}
      endIcon={<Iconify icon="solar:arrow-right-linear" width={18} />}
      sx={{ mt: 2.5, ml: -1.5 }}
    >
      {label}
    </CustomButton>
  );
}

// ----------------------------------------------------------------------

export function WelcomeStep({ ctx }: { ctx: StepContext }) {
  const theme = useTheme();
  const workspace = ctx.isSubAccountMember ? ctx.accountName || 'Your sub-tenant' : 'Organization (all workspaces)';
  const accessLabel = ctx.isOwner ? 'Organization owner' : ctx.roleNames.length ? ctx.roleNames.join(', ') : 'Member';
  const approvals: string[] = [];
  if (ctx.approvalPolicy?.requires_approval_for_publish) approvals.push('Publishing requires approval');
  if (ctx.approvalPolicy?.requires_approval_for_spend)
    approvals.push(`Spend of $${ctx.approvalPolicy.spend_threshold.toLocaleString()}+ requires approval`);

  const agenda = [
    { id: 'organization', icon: 'mdi:domain', title: 'Organization', text: 'How your company, sub-tenants and members fit together.' },
    { id: 'team', icon: 'mdi:shield-account-outline', title: 'Team & Access', text: 'Invite people, assign roles, approvals and audit logs.' },
    { id: 'subscription', icon: 'solar:card-linear', title: 'Subscription', text: 'Plans, usage limits, add-ons and billing rules.' },
    { id: 'wallet', icon: 'solar:wallet-linear', title: 'Wallet', text: 'The prepaid balance that funds campaigns and add-ons.' },
    ...(ctx.canAny(['integration.view', 'integration.edit'])
      ? [{ id: 'pixel', icon: 'mdi:radar', title: 'Tracking pixel', text: 'Connect your website with step-by-step install guides.' }]
      : []),
    { id: 'workflow', icon: 'solar:widget-5-linear', title: 'Modules', text: 'Every module available to you and how they connect.' },
    { id: 'finish', icon: 'solar:checklist-minimalistic-linear', title: 'Next steps', text: 'A personalized checklist to get you running.' },
  ];

  return (
    <>
      <StepHeader
        icon="solar:hand-shake-linear"
        overline="Welcome"
        title={ctx.orgName ? `Welcome to Boostt.ai, ${ctx.orgName}` : 'Welcome to Boostt.ai'}
        intro="Boostt.ai is an autonomous growth platform. Build audiences, launch campaigns across mail, email, ads and voice, then measure what worked. This tour takes about 3 minutes. Jump to any topic from the left, or come back to it anytime from your account menu."
      />

      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: 1,
          borderColor: alpha(theme.palette.primary.main, 0.3),
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Your access
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${ctx.orgName ? 3 : 2}, 1fr)` }, gap: 2 }}>
          {[
            ...(ctx.orgName ? [{ icon: 'mdi:office-building-outline', label: 'Organization', value: ctx.orgName }] : []),
            { icon: 'mdi:domain', label: 'Workspace', value: workspace },
            { icon: 'mdi:shield-account-outline', label: 'Access', value: accessLabel },
          ].map((f) => (
            <Stack key={f.label} direction="row" spacing={1.25} alignItems="flex-start">
              <Iconify icon={f.icon} width={20} sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.primary" sx={{ display: 'block' }}>
                  {f.label}
                </Typography>
                <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                  {f.value}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Box>
        {approvals.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 2 }}>
            {approvals.map((a) => (
              <Chip key={a} size="small" color="warning" variant="outlined" icon={<Iconify icon="mdi:clipboard-check-outline" width={16} />} label={a} />
            ))}
          </Stack>
        )}
      </Box>

      <SectionLabel>What you&apos;ll learn</SectionLabel>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        {agenda.map((a) => (
          <Box
            key={a.id}
            role="button"
            tabIndex={0}
            onClick={() => ctx.jumpToStep(a.id)}
            onKeyDown={(e) => e.key === 'Enter' && ctx.jumpToStep(a.id)}
            sx={{
              p: 2,
              borderRadius: 1.5,
              border: 1,
              borderColor: 'divider',
              cursor: 'pointer',
              transition: 'border-color 150ms, background-color 150ms',
              '&:hover, &:focus-visible': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04), outline: 'none' },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Iconify icon={a.icon} width={20} sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle2">{a.title}</Typography>
            </Stack>
            <Typography variant="body2" color="text.primary">
              {a.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
}

// ----------------------------------------------------------------------

export function OrganizationStep({ ctx }: { ctx: StepContext }) {
  return (
    <>
      <StepHeader
        icon="mdi:domain"
        overline="Organization"
        title="How your organization is structured"
        intro="Everything in Boostt.ai belongs to an organization. The owner can split the work into sub-tenants: separate workspaces for brands, teams or clients, each with its own people, campaigns and wallet."
      />
      <HierarchyDiagram orgName={ctx.orgName} />

      <SectionLabel>Key concepts</SectionLabel>
      <PointGrid points={ORG_CONCEPTS} />

      <SectionLabel>Sub-tenant types</SectionLabel>
      <PointGrid points={SUB_TENANT_TYPES} columns={3} />

      <SectionLabel>Switching between workspaces</SectionLabel>
      <PointGrid points={SCOPE_SWITCH_POINTS} columns={3} />
      <Callout tone="info" title={ctx.isOwner ? 'You are the organization owner' : 'Your workspace'}>
        {ctx.isOwner
          ? 'Use the selector in the top header to switch between the organization and any sub-tenant. Every switch reloads to the dashboard in that scope.'
          : ctx.isSubAccountMember
            ? `You work inside ${ctx.accountName || 'a sub-tenant'}. You see only its data, and the header shows your workspace as a read-only chip.`
            : 'You are an organization-level member. The header shows your workspace as a read-only chip.'}
      </Callout>

      {/* Agency markup is owner-only (CampaignMarkupField renders nothing for anyone else). */}
      {ctx.isOwner && (
        <>
          <SectionLabel>Agency markup</SectionLabel>
          <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
            Managing campaigns for clients? Add your agency fee on top of campaign costs. The client sees one marked-up
            price, and every cost-related metric in their workspace uses the marked-up value.
          </Typography>
          <MarkupExample />
          <Box sx={{ mt: 2 }}>
            <FlowSteps steps={AGENCY_MARKUP_FLOW} />
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1.25 }}>
            Where you set it
          </Typography>
          <PointGrid points={AGENCY_MARKUP_WHERE} />
          <Callout tone="tip" title="Works best with Agency sub-tenants">
            Create each client as an Agency sub-tenant with &quot;Allow agency markup&quot; on. You can also set markup on
            campaigns in your own Organization view.
          </Callout>
        </>
      )}

      <SectionLabel>Common questions</SectionLabel>
      <FaqList items={ctx.isOwner ? [...AGENCY_MARKUP_FAQ, ...ORG_FAQ] : ORG_FAQ} />
      <OpenLink ctx={ctx} label="Open Organization overview" path="/account/organization?section=overview" permission="member.view" />
    </>
  );
}

// ----------------------------------------------------------------------

export function TeamStep({ ctx }: { ctx: StepContext }) {
  return (
    <>
      <StepHeader
        icon="mdi:shield-account-outline"
        overline="Team & Access"
        title="Control who can do what"
        intro="Team & Access follows the same order as its sidebar: structure (sub-tenants), then people (members, groups), then access (roles, permissions), then workflow (approvals, audit)."
      />
      <SectionLabel>How it works</SectionLabel>
      <FlowSteps steps={TEAM_FLOW} />

      <SectionLabel>Access levels</SectionLabel>
      <SimpleTable
        head={['Level', 'Typically for', 'Can']}
        rows={ACCESS_LEVELS.map((l) => [l.level, l.who, l.can])}
      />
      <Typography variant="body2" color="text.primary" sx={{ mt: 1.25 }}>
        Levels come from the roles a member holds. Custom roles let you grant exactly the permissions a job needs.
      </Typography>

      <SectionLabel>Permissions</SectionLabel>
      <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
        Each permission is written as <b>resource.action</b>. For example, <code>campaign.publish</code> allows publishing
        campaigns. Roles bundle permissions, and the Permissions tab shows the full matrix.
      </Typography>
      <ChipRow items={PERMISSION_EXAMPLES} mono />

      <Callout tone="tip" title="Can't find something?">
        If a menu or button is missing, your active role or sub-tenant doesn&apos;t include that permission. The page
        isn&apos;t broken. Ask your organization owner or an admin to grant it.
      </Callout>

      <SectionLabel>Common questions</SectionLabel>
      <FaqList items={TEAM_FAQ} />
      <OpenLink ctx={ctx} label="Open Team & Access" path="/account/organization?section=members" permission="member.view" />
    </>
  );
}

// ----------------------------------------------------------------------

const fmtDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
const tierLabel = (tier?: string) => (tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : 'Your');

/** Live plan/trial status for the user (or the org owner's plan for sub-tenant members). */
function SubscriptionStatusCard({ ctx }: { ctx: StepContext }) {
  const theme = useTheme();
  const { subscription, isLoading } = useSubscription(ctx.pricingUserId);
  // The hook reports isLoading=false for the first render before its fetch starts;
  // wait until a fetch has started so "Not started" doesn't flash for subscribed users.
  const [fetchStarted, setFetchStarted] = useState(false);
  useEffect(() => {
    if (isLoading) setFetchStarted(true);
  }, [isLoading]);

  const shell = (tone: string, icon: string, overline: string, title: ReactNode, body: ReactNode, extra?: ReactNode, action?: ReactNode) => (
    <Box sx={{ p: 2.5, borderRadius: 2, border: 1, borderColor: alpha(tone, 0.35), bgcolor: alpha(tone, 0.06) }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <IconTile icon={icon} color={tone} size={48} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: tone, lineHeight: 1.6 }}>
            {overline}
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.primary">
            {body}
          </Typography>
        </Box>
        {action}
      </Stack>
      {extra}
    </Box>
  );

  if (isLoading || (ctx.pricingUserId != null && !fetchStarted)) {
    return (
      <Box sx={{ p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="text.primary">
          Checking your plan…
        </Typography>
        <LinearProgress sx={{ mt: 1.5, height: 4, borderRadius: 2 }} />
      </Box>
    );
  }

  const whose = ctx.isSubAccountMember ? "Your organization's plan" : 'Your plan';
  const status = subscription?.status;
  const isTrial = status === 'trial' || subscription?.is_trial === true;
  const cta = (label: string) =>
    ctx.canOpen('subscription.view') ? (
      <CustomButton variant="contained" onClick={() => ctx.goTo(ctx.subscriptionPath)} sx={{ flexShrink: 0 }}>
        {label}
      </CustomButton>
    ) : undefined;

  // Trial — the headline the subscribe flow ends on.
  if (subscription && isTrial) {
    const end = parseDateOnly(subscription.trial_ends_at) ?? parseDateOnly(subscription.current_period_end);
    const msDay = 86_400_000;
    const daysLeft = end ? Math.max(0, Math.ceil((end.getTime() - Date.now()) / msDay)) : null;
    const used = daysLeft == null ? 0 : Math.min(TRIAL_DAYS, Math.max(0, TRIAL_DAYS - daysLeft));
    return shell(
      theme.palette.success.main,
      'solar:gift-linear',
      `${whose} · Free trial`,
      end ? `Your free trial ends on ${fmtDate(end)}` : `You're on a ${TRIAL_DAYS}-day free trial`,
      <>
        {tierLabel(subscription.tier)} plan
        {daysLeft != null && ` · ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`}. Billing starts when the trial
        ends. To stop before then, use Manage Billing → Cancel Subscription and you won&apos;t be charged.
      </>,
      daysLeft != null && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={(used / TRIAL_DAYS) * 100}
            color="success"
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
            <Typography variant="caption" color="text.primary">
              Day {Math.max(1, used)} of {TRIAL_DAYS}
            </Typography>
            {end && (
              <Typography variant="caption" color="text.primary">
                Ends {fmtDate(end)}
              </Typography>
            )}
          </Stack>
        </Box>
      ),
      cta('View plan')
    );
  }

  if (subscription && status === 'active') {
    const endsAt = parseDateOnly(subscription.subscription_ends_at);
    if (subscription.cancellation_scheduled_at_period_end) {
      return shell(
        theme.palette.warning.main,
        'solar:calendar-mark-linear',
        whose,
        endsAt ? `Active until ${fmtDate(endsAt)}` : 'Cancelling at period end',
        `${tierLabel(subscription.tier)} plan. It won't renew after that date. Choose any plan to keep going.`,
        undefined,
        cta('Manage plan')
      );
    }
    const next = parseDateOnly(subscription.next_billing_date) ?? parseDateOnly(subscription.current_period_end);
    return shell(
      theme.palette.primary.main,
      'solar:crown-linear',
      whose,
      `${tierLabel(subscription.tier)} plan · Active`,
      next ? `Next billing date: ${fmtDate(next)}.` : 'Your subscription is active.',
      undefined,
      cta('View plan')
    );
  }

  if (subscription && (status === 'past_due' || status === 'suspended' || status === 'pending')) {
    return shell(
      theme.palette.error.main,
      'solar:danger-triangle-linear',
      whose,
      status === 'pending' ? 'Your subscription is pending' : 'Your payment needs attention',
      'Update your payment method under Manage Billing, or choose a plan again to restore full access.',
      undefined,
      cta('Fix billing')
    );
  }

  // No plan yet (or cancelled).
  return shell(
    theme.palette.info.main,
    'solar:rocket-2-linear',
    status === 'cancelled' ? `${whose} · Cancelled` : `${whose} · Not started`,
    ctx.isSubAccountMember ? 'Your organization has no active plan yet' : `Start your ${TRIAL_DAYS}-day free trial`,
    ctx.isSubAccountMember
      ? 'Ask your organization owner to choose a plan. Your sub-tenant uses it automatically.'
      : 'Choose a plan and add your card to complete your subscription. Your free trial and its end date then appear here and on your plan card.',
    undefined,
    ctx.isSubAccountMember ? undefined : cta('Choose a plan')
  );
}

export function SubscriptionStep({ ctx }: { ctx: StepContext }) {
  return (
    <>
      <StepHeader
        icon="solar:card-linear"
        overline="Subscription"
        title="Your plan, free trial and billing"
        intro="Your subscription sets which features you get and how much of each is included. Choose a plan, add a card, and your 2-week free trial starts. Everything lives under Subscription & Usage in the account menu."
      />
      <SubscriptionStatusCard ctx={ctx} />

      {ctx.isSubAccountMember ? (
        <Callout tone="info" title="Your sub-tenant uses the organization's plan">
          Usage limits and features are shared with the organization owner, who manages the plan. You can view it, but
          changes are made by the owner.
        </Callout>
      ) : (
        <>
          <SectionLabel>How to subscribe</SectionLabel>
          <FlowSteps steps={SUBSCRIBE_FLOW} />

          <SectionLabel>Adding a payment method</SectionLabel>
          <PointGrid points={PAYMENT_METHOD_POINTS} />
          <Callout tone="warning" title="The Pay button stays disabled until you have a default card">
            If you see &quot;Payment method required&quot; in the plan dialog, click Add Card (or Add Bank Account). A
            bank account can only be used after its micro-deposits are verified.
          </Callout>
        </>
      )}

      <SectionLabel>Plans</SectionLabel>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
        {PLAN_TIERS.map((t, i) => (
          <Box key={t.name} sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: 'divider', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: 'primary.main', opacity: 0.25 + i * 0.25 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {t.name}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {t.text}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography variant="body2" color="text.primary" sx={{ mt: 1.5, mb: 1 }}>
        Plans differ by:
      </Typography>
      <ChipRow items={ctx.showBlyn ? PLAN_DIFFERENTIATORS : PLAN_DIFFERENTIATORS.filter((d) => !/blyn/i.test(d))} />

      <SectionLabel>Billing rules</SectionLabel>
      <PointGrid points={BILLING_RULES} />
      <Callout tone="info" title="Plan vs. wallet">
        Your plan fee is charged through Stripe to your card or bank account, never the wallet. Add-ons, usage overages
        and extra AI credits are paid from your wallet.
      </Callout>

      <SectionLabel>Add-on modules</SectionLabel>
      <PointGrid points={ctx.showBlyn ? ADD_ONS : ADD_ONS.filter((a) => !/blyn/i.test(a.title))} />
      <Typography variant="body2" color="text.primary" sx={{ mt: 1.25 }}>
        Add-ons are charged to your wallet, prorated for the rest of the month. Availability and price depend on your plan.
      </Typography>

      <SectionLabel>Reading your usage</SectionLabel>
      <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
        Every metered service (contacts, platform events, emails, postcards, AI tokens, storage, API calls and more) shows
        how much of its limit you have used this period:
      </Typography>
      <CapacityScale />

      <SectionLabel>Common questions</SectionLabel>
      <FaqList items={SUBSCRIPTION_FAQ} />
      <OpenLink ctx={ctx} label="Open Subscription & Usage" path={ctx.subscriptionPath} permission="subscription.view" />
    </>
  );
}

// ----------------------------------------------------------------------

export function WalletStep({ ctx }: { ctx: StepContext }) {
  return (
    <>
      <StepHeader
        icon="solar:wallet-linear"
        overline="Wallet"
        title="Fund your campaigns"
        intro="The wallet is a prepaid balance. Campaign sends, data purchases and add-ons draw from it, so keeping it topped up keeps everything running."
      />
      {ctx.isSubAccountMember && (
        <Callout tone="info" title="Your sub-tenant has its own wallet">
          Its balance, payment methods and history are separate from the organization&apos;s wallet.
        </Callout>
      )}

      <SectionLabel>How money flows</SectionLabel>
      <FlowSteps steps={WALLET_FLOW} />

      <SectionLabel>Auto-recharge</SectionLabel>
      <AutoRechargeExample />

      <SectionLabel>What the wallet pays for</SectionLabel>
      <ChipRow items={WALLET_SPEND_CATEGORIES} />

      <Callout tone="warning" title="Add a payment method first">
        Recharge and auto-recharge only work after you add a payment method. Card recharges show a 3% processing fee
        before you confirm.
      </Callout>

      <SectionLabel>Common questions</SectionLabel>
      <FaqList items={WALLET_FAQ} />
      <OpenLink ctx={ctx} label="Open Wallet" path={ctx.walletPath} permission="billing.view" />
    </>
  );
}

// ----------------------------------------------------------------------

export function PixelStep({ ctx }: { ctx: StepContext }) {
  const theme = useTheme();
  const focus = useContext(TourFocusContext);
  const focusedGuide = PIXEL_PLATFORM_GUIDES.find((g) => g.label === focus);
  const [platform, setPlatform] = useState(focusedGuide?.id ?? PIXEL_PLATFORM_GUIDES[0].id);
  useEffect(() => {
    if (focusedGuide) setPlatform(focusedGuide.id);
  }, [focusedGuide]);
  const guide = PIXEL_PLATFORM_GUIDES.find((g) => g.id === platform) ?? PIXEL_PLATFORM_GUIDES[0];

  return (
    <>
      <StepHeader
        icon="mdi:radar"
        overline="Tracking pixel"
        title="Connect your website"
        intro="One small script on your website connects site visits to your campaigns. There is one pixel per account, and it covers every campaign on that account, so you install it once."
      />

      <SectionLabel>How it works</SectionLabel>
      <FlowSteps steps={PIXEL_FLOW} />

      <SectionLabel>Your pixel</SectionLabel>
      <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
        {ctx.isSubAccountMember
          ? `This is the pixel for ${ctx.accountName || 'your sub-tenant'}.`
          : 'This is the pixel for the workspace you are in now. Switch into a sub-tenant first to get that client’s pixel.'}{' '}
        Generate it if you haven&apos;t yet, then copy the embed code.
      </Typography>
      <Box sx={{ borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
        <TrackingPixelTab />
      </Box>

      <SectionLabel>Install it on your website</SectionLabel>
      <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
        Pick your website platform for exact steps.
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
        {PIXEL_PLATFORM_GUIDES.map((g) => (
          <Chip
            key={g.id}
            icon={<Iconify icon={g.icon} width={16} />}
            label={g.label}
            onClick={() => setPlatform(g.id)}
            color={g.id === platform ? 'primary' : 'default'}
            variant={g.id === platform ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>
      <Box data-search-title={guide.label} sx={{ p: 2.5, borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Iconify icon={guide.icon} width={20} sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight={700}>
            {guide.label}
          </Typography>
        </Stack>
        <Stack spacing={1.25}>
          {guide.steps.map((stepText, i) => (
            <Stack key={stepText} direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  color: 'primary.main',
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {i + 1}
              </Box>
              <Typography variant="body2" color="text.primary" sx={{ pt: 0.25 }}>
                {stepText}
              </Typography>
            </Stack>
          ))}
        </Stack>
        {guide.note && (
          <Typography variant="caption" color="text.primary" sx={{ display: 'block', mt: 1.75 }}>
            Note: {guide.note}
          </Typography>
        )}
      </Box>

      <SectionLabel>Check it&apos;s working</SectionLabel>
      <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
        After installing, visit a few pages on your site, then click <b>Check if pixel is firing</b> in the pixel card
        above to see recent activity. If nothing shows up:
      </Typography>
      <PointGrid points={PIXEL_TROUBLESHOOTING} />

      <SectionLabel>What it unlocks</SectionLabel>
      <PointGrid points={PIXEL_USES} />

      <SectionLabel>Common questions</SectionLabel>
      <FaqList items={PIXEL_FAQ} />
      <OpenLink ctx={ctx} label="Open Integrations → Tracking Pixel" path={PIXEL_PATH} />
    </>
  );
}

// ----------------------------------------------------------------------

export function WorkflowStep({ ctx }: { ctx: StepContext }) {
  const theme = useTheme();
  const available = new Set(ctx.modules.map((m) => m.key));
  return (
    <>
      <StepHeader
        icon="solar:widget-5-linear"
        overline="Modules"
        title="How the modules fit together"
        intro="Modules are grouped into families in the left sidebar. Most campaigns follow this path, and each stage lives in a family."
      />
      <Box sx={{ position: 'relative', pl: 1 }}>
        {WORKFLOW.map((w, i) => {
          const fam = FAMILIES[w.family];
          const has = available.has(w.family);
          return (
            <Stack key={w.title} data-search-title={w.title} direction="row" spacing={2} sx={{ position: 'relative', pb: i < WORKFLOW.length - 1 ? 2.5 : 0 }}>
              {i < WORKFLOW.length - 1 && (
                <Box sx={{ position: 'absolute', left: 21, top: 44, bottom: 0, width: 2, bgcolor: 'divider' }} />
              )}
              <IconTile icon={w.icon} color={has ? fam.color : theme.palette.text.disabled} />
              <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle1" fontWeight={700}>
                    {w.title}
                  </Typography>
                  <Chip
                    size="small"
                    label={fam.label}
                    onClick={has ? () => ctx.jumpToStep(`module-${w.family}`) : undefined}
                    sx={{ bgcolor: alpha(fam.color, 0.14), color: 'text.primary', fontWeight: 600 }}
                  />
                  {!has && <Chip size="small" variant="outlined" label="Not in your access" />}
                </Stack>
                <Typography variant="body2" color="text.primary">
                  {w.text}
                </Typography>
              </Box>
            </Stack>
          );
        })}
      </Box>

      <SectionLabel>Your modules</SectionLabel>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        {ctx.modules.map((m) => {
          const fam = FAMILIES[m.key];
          return (
            <Box
              key={m.key}
              role="button"
              tabIndex={0}
              onClick={() => ctx.jumpToStep(`module-${m.key}`)}
              onKeyDown={(e) => e.key === 'Enter' && ctx.jumpToStep(`module-${m.key}`)}
              sx={{
                p: 2,
                borderRadius: 1.5,
                border: 1,
                borderColor: 'divider',
                borderTop: `3px solid ${fam.color}`,
                cursor: 'pointer',
                '&:hover, &:focus-visible': { bgcolor: alpha(fam.color, 0.06), outline: 'none' },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Iconify icon={fam.icon} width={20} sx={{ color: fam.color }} />
                <Typography variant="subtitle1" fontWeight={700}>
                  {fam.label}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.primary" sx={{ display: 'block', mt: 0.25 }}>
                {fam.tagline} · {countItems(m.items)} tools
              </Typography>
            </Box>
          );
        })}
      </Box>
    </>
  );
}

function countItems(items: NavItemLike[]) {
  return items.reduce((n, i) => n + (i.children?.length || 1), 0);
}

// ----------------------------------------------------------------------

export function ModuleStep({ ctx, group }: { ctx: StepContext; group: ModuleGroup }) {
  const fam = FAMILIES[group.key];

  const leafRow = (leaf: NavLeaf, nested: boolean) => (
    <Stack
      key={leaf.path}
      data-search-title={leaf.title}
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      onClick={() => ctx.goTo(leaf.path)}
      sx={{
        p: nested ? 1.25 : 2,
        borderRadius: 1.5,
        cursor: 'pointer',
        ...(nested ? {} : { border: 1, borderColor: 'divider' }),
        '&:hover': { bgcolor: alpha(fam.color, 0.06) },
        '&:hover .open-arrow': { opacity: 1, transform: 'translateX(2px)' },
      }}
    >
      {!nested && leaf.icon && (
        <Box sx={{ color: fam.color, display: 'flex', mt: 0.25, '& svg, & img, & span': { width: 22, height: 22 } }}>{leaf.icon}</Box>
      )}
      {nested && <Iconify icon="solar:alt-arrow-right-linear" width={14} sx={{ color: fam.color, mt: 0.5, flexShrink: 0 }} />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2">{leaf.title}</Typography>
          {leaf.info && <Chip label={leaf.info} size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
        </Stack>
        {ITEM_DESCRIPTIONS[leaf.path] && (
          <Typography variant="body2" color="text.primary">
            {ITEM_DESCRIPTIONS[leaf.path]}
          </Typography>
        )}
      </Box>
      <Iconify
        className="open-arrow"
        icon="solar:arrow-right-up-linear"
        width={18}
        sx={{ color: fam.color, opacity: 0.4, transition: 'all 150ms', flexShrink: 0, mt: 0.25 }}
      />
    </Stack>
  );

  return (
    <>
      <StepHeader icon={fam.icon} overline={fam.tagline} title={fam.label} intro={fam.overview} color={fam.color} />

      <SectionLabel>Key capabilities</SectionLabel>
      <PointGrid points={fam.capabilities} columns={fam.capabilities.length % 3 === 0 ? 3 : 2} color={fam.color} />

      <SectionLabel>Get started in {fam.getStarted.length} steps</SectionLabel>
      <FlowSteps steps={fam.getStarted} color={fam.color} />

      <Callout tone="tip" title="Tip">
        {fam.tip}
      </Callout>

      <SectionLabel>What&apos;s inside ({countItems(group.items)} tools)</SectionLabel>
      <Stack spacing={1.5}>
        {group.items.map((item) =>
          item.children?.length ? (
            <Box key={item.title} sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                {item.icon && (
                  <Box sx={{ color: fam.color, display: 'flex', '& svg, & img, & span': { width: 22, height: 22 } }}>{item.icon}</Box>
                )}
                <Typography variant="subtitle1" fontWeight={700}>
                  {item.title}
                </Typography>
                {item.info && <Chip label={item.info} size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                <Typography variant="caption" color="text.primary">
                  {item.children.length} tools
                </Typography>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.5, borderLeft: `2px solid ${alpha(fam.color, 0.3)}`, pl: 1 }}>
                {item.children.map((c) => leafRow(c, true))}
              </Box>
            </Box>
          ) : (
            leafRow(item, false)
          )
        )}
      </Stack>
      <Typography variant="caption" color="text.primary" sx={{ display: 'block', mt: 2 }}>
        Click any tool to open it. You only see tools your role allows.
      </Typography>

      {fam.faq.length > 0 && (
        <>
          <SectionLabel>Common questions</SectionLabel>
          <FaqList items={fam.faq} />
        </>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

export function AccountSettingsStep({ ctx }: { ctx: StepContext }) {
  const visible = ACCOUNT_SETTINGS.filter((s) => ctx.canAny(s.permissions));
  return (
    <>
      <StepHeader
        icon="solar:settings-linear"
        overline="Account settings"
        title="Make the platform yours"
        intro="Besides Subscription, Wallet and Team & Access, the account menu (your avatar, top right) has settings that shape every campaign."
      />
      <Stack spacing={1.5}>
        {visible.map((s) => (
          <Stack key={s.title} data-search-title={s.title} direction="row" spacing={2} alignItems="center" sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
            <IconTile icon={s.icon} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {s.title}
              </Typography>
              <Typography variant="body2" color="text.primary">
                {s.text}
              </Typography>
            </Box>
            <CustomButton variant="outlined" size="small" onClick={() => ctx.goTo(s.path)} sx={{ flexShrink: 0 }}>
              Open
            </CustomButton>
          </Stack>
        ))}
      </Stack>
      {visible.length === 0 && (
        <Callout tone="info">Account settings are managed by your organization owner or admin.</Callout>
      )}
      <Callout tone="tip" title="Tip">
        Set up a Brand Kit before your first campaign so creatives and emails use your colors, fonts and logo
        automatically.
      </Callout>
    </>
  );
}

// ----------------------------------------------------------------------

export function FinishStep({ ctx }: { ctx: StepContext }) {
  const theme = useTheme();
  const tasks = [
    { show: ctx.canOpen('billing.view'), icon: 'solar:card-linear', title: 'Add a payment method and turn on auto-recharge', path: ctx.walletPath },
    { show: ctx.canOpen('subscription.view') && !ctx.isSubAccountMember, icon: 'solar:crown-linear', title: 'Review your plan and add-ons', path: ctx.subscriptionPath },
    { show: ctx.isOwner || ctx.canOpen('account.create_child'), icon: 'mdi:domain', title: 'Create sub-tenants for your brands, teams or clients', path: '/account/organization?section=accounts' },
    { show: ctx.canOpen('member.invite'), icon: 'mdi:account-plus-outline', title: 'Invite your team and assign roles', path: '/account/organization?section=members' },
    { show: ctx.canOpen('settings.view'), icon: 'solar:palette-linear', title: 'Create a Brand Kit', path: '/account/brands' },
    { show: ctx.canAny(['integration.view', 'integration.edit']), icon: 'mdi:radar', title: 'Install your tracking pixel on your website', path: PIXEL_PATH },
    { show: ctx.canAny(['integration.view', 'integration.edit']), icon: 'solar:plug-circle-linear', title: 'Connect your CRM and data sources', path: '/integrations' },
    { show: ctx.canOpen('audience.import'), icon: 'solar:upload-linear', title: 'Upload your first audience', path: '/cdm/upload-audience' },
    { show: ctx.canOpen('campaign.view'), icon: 'solar:clapperboard-linear', title: 'Launch your first campaign', path: '/campaigns' },
  ].filter((t) => t.show);

  return (
    <>
      <StepHeader
        icon="solar:checklist-minimalistic-linear"
        overline="You're ready"
        title="Your next steps"
        intro="Based on your access, here is where we recommend you start. Each item opens the right page."
      />
      <Stack spacing={1}>
        {tasks.map((t, i) => (
          <Stack
            key={t.title}
            direction="row"
            spacing={2}
            alignItems="center"
            onClick={() => ctx.goTo(t.path)}
            sx={{
              p: 1.75,
              borderRadius: 1.5,
              border: 1,
              borderColor: 'divider',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                border: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 700,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {i + 1}
            </Box>
            <Iconify icon={t.icon} width={22} sx={{ color: 'primary.main', flexShrink: 0 }} />
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {t.title}
            </Typography>
            <Iconify icon="solar:arrow-right-linear" width={18} sx={{ color: 'primary.main' }} />
          </Stack>
        ))}
      </Stack>
      <Callout tone="tip" title="Come back anytime">
        Reopen this tour from your account menu (Platform tour). For a detailed explanation of access, see Team &amp;
        Access → How it works.
      </Callout>
    </>
  );
}
